import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { alpha } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import { useTranslation } from "../../hooks";
import { usePortalStore } from "../../store/portal/PortalStoreContext";
import { useRescheduleAppointmentMutation } from "../../queries/portal/usePortalMutations";
import { useSlotsQuery } from "../../queries/useSlotsQuery";
import { useWorkspaceQuery } from "../../queries/useWorkspaceQuery";
import { extractApiError } from "../../utils/extractApiError";
import { DateStrip, toIsoDate } from "./DateStrip";
import { SlotGrid } from "./SlotGrid";
import { PortalSection } from "./ui/PortalSection";
import { formatAppointmentDate, formatAppointmentTime, } from "./appointmentPresentation";
const DATE_STRIP_DAYS = 14;
export function PortalRescheduleStep() {
    const { t } = useTranslation("portal");
    const appointment = usePortalStore((s) => s.rescheduleTarget);
    const cancelReschedule = usePortalStore((s) => s.cancelReschedule);
    const [date, setDate] = React.useState(() => toIsoDate(new Date()));
    const [selectedIndex, setSelectedIndex] = React.useState(null);
    const workspaceQuery = useWorkspaceQuery();
    const mutation = useRescheduleAppointmentMutation();
    const doctorId = appointment?.doctorId ?? null;
    const addressId = appointment?.addressId ?? null;
    const workspaceId = workspaceQuery.data?.workspaceId ?? null;
    const slotsQuery = useSlotsQuery({
        workspaceId,
        addressId,
        doctorId,
        date,
        slotFetchMode: "scheduled",
        invalidationKey: 0,
    });
    React.useEffect(() => {
        setSelectedIndex(null);
    }, [date]);
    if (!appointment)
        return null;
    const slots = slotsQuery.data?.slots ?? [];
    const submitting = mutation.isPending;
    const error = mutation.isError
        ? extractApiError(mutation.error, "We could not move that appointment.")
        : null;
    const handleConfirm = () => {
        if (selectedIndex === null || doctorId == null || addressId == null)
            return;
        const slot = slots[selectedIndex];
        if (!slot)
            return;
        mutation.beginAttempt();
        mutation.mutate({
            appointmentId: appointment.id,
            doctorId,
            addressId,
            body: {
                appointmentDate: slot.appointmentDate ?? date,
                fromDateTimeTs: slot.fromDateTimeTs ?? slot.fromDateTimeIso,
                toDateTimeTs: slot.toDateTimeTs ?? slot.toDateTimeIso,
                mode: appointment.mode ?? undefined,
            },
        }, { onSuccess: cancelReschedule });
    };
    return (_jsxs(Box, { children: [_jsx(Button, { onClick: cancelReschedule, disabled: submitting, startIcon: _jsx(ArrowBackRoundedIcon, { sx: { fontSize: 18 } }), sx: { mb: 2, px: 1, color: "text.secondary" }, children: t("backToAppointments", "Back to my appointments") }), _jsx(Box, { sx: (theme) => ({
                    borderRadius: 2,
                    p: { xs: 1.75, sm: 2.25 },
                    mb: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }), children: _jsxs(Stack, { direction: "row", spacing: 1.5, sx: { alignItems: "flex-start" }, children: [_jsx(Box, { "aria-hidden": "true", sx: (theme) => ({
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 34,
                                height: 34,
                                flexShrink: 0,
                                borderRadius: 2,
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                "& svg": { fontSize: 18 },
                            }), children: _jsx(EventRepeatRoundedIcon, {}) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                        color: "text.secondary",
                                    }, children: t("movingAppointment", "Moving this appointment") }), _jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, mt: 0.25 }, children: appointment.doctorName || "—" }), _jsx(Typography, { variant: "body2", sx: { color: "text.secondary", wordBreak: "break-word" }, children: [
                                        formatAppointmentDate(appointment),
                                        formatAppointmentTime(appointment),
                                        appointment.locationAddress,
                                    ]
                                        .filter(Boolean)
                                        .join(" · ") })] })] }) }), _jsx(PortalSection, { title: t("pickDate", "Pick a new day"), gap: 2, children: _jsx(DateStrip, { value: date, onChange: setDate, days: DATE_STRIP_DAYS, disabled: submitting }) }), _jsx(PortalSection, { title: t("pickTime", "Pick a new time"), gap: 3, children: _jsx(SlotGrid, { slots: slots, selectedIndex: selectedIndex, onSelect: setSelectedIndex, loading: slotsQuery.isPending, error: slotsQuery.isError, onRetry: () => slotsQuery.refetch(), disabled: submitting }) }), error ? (_jsx(Alert, { severity: "error", sx: { mb: 2, borderRadius: 2 }, children: error })) : null, _jsx(Box, { sx: (theme) => ({
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1,
                    pt: 2,
                    pb: 1,
                    backgroundColor: theme.palette.background.default,
                    maskImage: "linear-gradient(to bottom, transparent 0, #000 16px)",
                }), children: _jsx(Button, { fullWidth: true, size: "large", variant: "contained", disabled: selectedIndex === null || submitting, onClick: handleConfirm, sx: { py: 1.25, borderRadius: 2, fontSize: 15 }, children: submitting ? (_jsx(CircularProgress, { size: 20, color: "inherit" })) : (t("confirmReschedule", "Confirm reschedule")) }) })] }));
}
